import { Group, Mesh, PlaneGeometry, ShaderMaterial, MeshBasicMaterial, Vector3, Vector2, AdditiveBlending, MathUtils } from 'three';
import Resources from './Resources.js';
import Camera from './Camera.js';
import Sizes from '../utils/Sizes.js';
import Input from '../utils/Input.js';
import vertexShader from '../shaders/phone/vertex.glsl';
import fragmentShader from '../shaders/phone/fragment.glsl';
import triangleVertexShader from '../shaders/triangle/vertex.glsl';
import triangleFragmentShader from '../shaders/triangle/fragment.glsl';
import startupVertexShader from '../shaders/startup/vertex.glsl';
import startupFragmentShader from '../shaders/startup/fragment.glsl';
import hotlineVertexShader from '../shaders/hotline/vertex.glsl';
import hotlineFragmentShader from '../shaders/hotline/fragment.glsl';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';

export default class Phone extends Group {
    constructor() {
        super();

        
        // Initialize resources
        this.resources = Resources.getInstance();
        
        // Get camera and sizes
        this.camera = Camera.getInstance().cameraForeground;
        this.sizes = Sizes.getInstance();
        this.input = Input.getInstance();

        // Content width configuration
        this.contentWrapper = {
            maxWidth: 1300, // Updated to 1300px as requested
            padding: 40,    // Padding from the right edge in pixels
        };
        
        // Unified responsive configuration
        this.responsiveConfig = {
            // Format: { breakpoint: minWidth, scale: sizeCoefficient, padding: paddingValue }
            // Ordered from smallest to largest breakpoint
            breakpoints: [
                { breakpoint: 0, scale: 0.8, padding: 20 },     // Mobile: < 480px
                { breakpoint: 480, scale: 0.8, padding: 20 },   // Mobile large: 480px - 767px
                { breakpoint: 768, scale: 0.7, padding: 60 },    // Tablet: 768px - 1023px
                { breakpoint: 1024, scale: 0.8, padding: 100 },   // Small desktop: 1024px - 1199px
                { breakpoint: 1200, scale: 0.8, padding: 80 }    // Desktop: >= 1200px (using wrapper padding)
            ]
        };
        
        // Current scale coefficient
        this.currentScale = this.getResponsiveValue('scale');

        // Animation state
        this.isAppearing = true;
        this.appearDuration = 1.5; // seconds
        this.animationTime = 0;
        this.currentProgressPhone = 0; // The actual value sent to shader, interpolated
        this.targetProgressPhone = 0;  // The value we are lerping towards
        this.currentProgressTriangle = 0; // The actual value sent to shader, interpolated
        this.targetProgressTriangle = 0;  // The value we are lerping towards
        this.lerpFactor = 0.05;   // Smoothing factor for progress
        this.lerpFactorTriangle = 0.01;   // Smoothing factor for progress
        this.triangleOpacity = 0; // Starting opacity for triangle
        this.phoneOpacity = 0;    // Starting opacity for phone materials
        this.titleOpacity = 0;    // Starting opacity for title

        // Star animation parameters
        this.starAnimationStartTime = 1.8; // Start after titles are fully visible
        this.simplex = new SimplexNoise();
        this.starAnimState = {
            time: 0,
            noiseOffset: Math.random() * 100, // Random offset for noise
        };

        // Star parameters
        this.stars = [
            {
                name: 'smallStar1',
                texture: 'star',
                position: { x: -0.16, y: -0.12, z: 0.03 },
                size: 0.06, // percent of phone size
                scale: 1.0, // for animation
                opacity: 0.8
            },
            {
                name: 'smallStar2',
                texture: 'star',
                position: { x: 0.02, y: -0.05, z: 0.03 },
                size: 0.05, // percent of phone size
                scale: 1.0, // for animation
                opacity: 0.7
            },
            {
                name: 'bigStar1',
                texture: 'starBig',
                position: { x: 0.21, y: -0.11, z: 0.03 },
                size: 0.2, // percent of phone size
                scale: 1.0, // for animation
                opacity: 0.9
            },
            {
                name: 'bigStar2',
                texture: 'starBig',
                position: { x: -0.33, y: -0.05, z: 0.03 },
                size: 0.24, // percent of phone size
                scale: 1.0, // for animation
                opacity: 0.8
            }
        ];

        // Startup title parameters
        this.titleStartup = {
            scaleCoef: 0.6,
            opacity: 0,
            position: {
                x: -0.03,
                y: -0.09,
                z: 0
            },
            initialShift: {
                x: 0.1,  // Initial X shift to the right
                y: 0      // No vertical shift
            },
            animationDuration: 1.5,
            animationStartDelay: 0.5,
            isAnimating: true
        }

        // Hotline title parameters
        this.titleHotline = {
            scaleCoef: 0.36,
            opacity: 0,
            position: {
                x: 0.07,
                y: -0.14,
                z: 0
            },
            initialShift: {
                x: 0,  // Initial X shift to the right
                y: 0      // No vertical shift
            },
            animationDuration: 1.5,
            animationStartDelay: 0.8, // Start slightly after titleStartup
            isAnimating: true
        }

        // Mouse control parameters
        this.mouseControlRangeStart = 0.6; // 60%
        this.mouseControlRangeEnd = 1.0;   // 100%
        this.mouseControlCenterProgress = 0.8; // 80% - progress when mouse is at center
        this.mouseControlTriangleRangeStart = 0.0; // 0% - triangle starts at 0
        this.mouseControlTriangleRangeEnd = 1.0;   // 100% - triangle ends at 1
        this.sensitivityPowerRight = 0.3; // Controls mouse sensitivity falloff (right side)
        this.sensitivityPowerLeft = 1.1;  // Controls mouse sensitivity falloff (left side)

        // Idle animation parameters
        this.isIdleAnimation = true; // Start with idle animation until user moves mouse
        this.idleAnimationTime = 0;
        this.idleAnimationSpeed = 0.3; // Speed of idle animation
        this.idleAnimationRange = 0.1; // Max deviation from center during idle (±)
        this.idleStartProgress = this.mouseControlCenterProgress; // Store where the idle animation started
        
        // For smooth transitions
        this.lastNonIdletargetProgressPhone = this.mouseControlCenterProgress;
        this.idleTransitionStartTime = 0;
        this.idleTransitionDuration = 2.0; // Seconds to fully transition to oscillating around center
        this.idleEasingPower = 1.0; // Power for easing function (1.0 = Power1)
        
        // Figure-8 floating animation parameters
        this.floatingAnimationTime = 0;
        this.floatingSpeed = 0.5; // Speed of floating animation
        this.floatingAmplitudeX = 0.005; // Horizontal amplitude of figure-8
        this.floatingAmplitudeY = 0.02; // Vertical amplitude of figure-8
        this.enableFloating = true; // Enable/disable floating animation

        // Listen for idle state changes
        this.input.on('changeIdle', this.handleIdleStateChange.bind(this));

        // Listen for resize events
        this.sizes.on('resize', this.onResize.bind(this));

        this.resources.on('ready', () => {
            this.init();
        });
    }

    // Get the appropriate value based on current screen width and property name
    getResponsiveValue(property) {
        const screenWidth = this.sizes.width;
        
        // Start with the smallest breakpoint
        let value = this.responsiveConfig.breakpoints[0][property];
        
        // Find the appropriate breakpoint
        for (const bp of this.responsiveConfig.breakpoints) {
            if (screenWidth >= bp.breakpoint) {
                value = bp[property];
            } else {
                break; // Exit once we find a breakpoint larger than the screen width
            }
        }
        
        return value;
    }
    
    // Handle resize events
    onResize() {
        // Update scale based on new screen size
        const newScale = this.getResponsiveValue('scale');
        
        // If scale changed, resize the phone
        if (newScale !== this.currentScale) {
            this.currentScale = newScale;
            this.resizePhone();
        }
        
        // Update position
        this.updatePosition();
        
        // Update visibility
        this.updateVisibility();
    }
    
    // Resize the phone mesh based on the current scale
    resizePhone() {
        if (!this.phoneBackgroundMesh) return;
        
        // Get current camera parameters
        const fov = this.camera.fov * (Math.PI / 180);
        const distance = Math.abs(this.camera.position.z);
        const height = 2 * Math.tan(fov / 2) * distance;
        const width = height * this.sizes.aspectRatio;
        
        // Calculate new size based on responsive scale
        const planeSize = Math.min(height, width) * this.currentScale;
        
        // Update all meshes with the new size
        this.resizeMesh(this.phoneBackgroundMesh, planeSize, planeSize);
        this.resizeMesh(this.phoneForegroundMesh, planeSize, planeSize);
        this.resizeMesh(this.triangleMesh, planeSize, planeSize);
        
        // Resize stars
        if (this.starMeshes) {
            this.updateStarSizes(planeSize);
        }
        
        // Update titles with new phone size
        this.updateTitleSizes(planeSize);
    }
    
    // Helper to resize a mesh
    resizeMesh(mesh, width, height) {
        if (!mesh) return;
        
        // Create new geometry with the target size
        const newGeometry = new PlaneGeometry(width, height);
        
        // Dispose of old geometry to prevent memory leaks
        mesh.geometry.dispose();
        
        // Assign new geometry
        mesh.geometry = newGeometry;
    }
    
    // Update star sizes based on new phone size
    updateStarSizes(phonePlaneSize) {
        if (!this.starMeshes || this.starMeshes.length === 0) return;
        
        for (let i = 0; i < this.starMeshes.length; i++) {
            const starMesh = this.starMeshes[i];
            const star = this.stars[i];
            
            // Get the star texture
            const starTexture = this.resources.items[star.texture];
            
            // Calculate size based on phone size and star's size percentage
            const baseSize = phonePlaneSize * star.size;
            
            // Calculate aspect ratio of the texture
            const imageAspectRatio = starTexture.image.width / starTexture.image.height;
            
            // Calculate width and height based on the aspect ratio
            let starWidth, starHeight;
            
            if (imageAspectRatio >= 1) {
                starWidth = baseSize;
                starHeight = baseSize / imageAspectRatio;
            } else {
                starWidth = baseSize * imageAspectRatio;
                starHeight = baseSize;
            }
            
            // Resize the star mesh
            this.resizeMesh(starMesh, starWidth, starHeight);
            
            // Update position based on percentage of phone size
            starMesh.position.x = phonePlaneSize * star.position.x;
            starMesh.position.y = phonePlaneSize * star.position.y;
        }
    }
    
    // Update title sizes based on new phone size
    updateTitleSizes(phonePlaneSize) {
        // Update startup title
        if (this.titleStartupMesh && this.resources.items.titleStartup) {
            const titleTexture = this.resources.items.titleStartup;
            const imageAspectRatio = titleTexture.image.width / titleTexture.image.height;
            const titleWidth = phonePlaneSize * this.titleStartup.scaleCoef;
            const titleHeight = titleWidth / imageAspectRatio;
            
            this.resizeMesh(this.titleStartupMesh, titleWidth, titleHeight);
            
            // Update position
            this.titleStartupMesh.position.x = phonePlaneSize * this.titleStartup.position.x;
            this.titleStartupMesh.position.y = phonePlaneSize * this.titleStartup.position.y;
        }
        
        // Update hotline title
        if (this.titleHotlineMesh && this.resources.items.titleHotline) {
            const titleTexture = this.resources.items.titleHotline;
            const imageAspectRatio = titleTexture.image.width / titleTexture.image.height;
            const titleWidth = phonePlaneSize * this.titleHotline.scaleCoef;
            const titleHeight = titleWidth / imageAspectRatio;
            
            this.resizeMesh(this.titleHotlineMesh, titleWidth, titleHeight);
            
            // Update position
            this.titleHotlineMesh.position.x = phonePlaneSize * this.titleHotline.position.x;
            this.titleHotlineMesh.position.y = phonePlaneSize * this.titleHotline.position.y;
        }
    }

    // Power easing function (equivalent to GSAP's Power eases)
    easeInOut(t, power = 1) {
        if (t < 0.5) {
            // Ease in for first half
            return Math.pow(2 * t, power) / 2;
        } else {
            // Ease out for second half
            return 1 - Math.pow(2 * (1 - t), power) / 2;
        }
    }

    handleIdleStateChange(isIdle) {
        // Store the previous state before updating
        const wasIdle = this.isIdleAnimation;
        this.isIdleAnimation = isIdle;
        
        if (isIdle && !wasIdle) {
            // Transitioning from active to idle
            
            // Store the current target as our starting point
            this.lastNonIdletargetProgressPhone = this.targetProgressPhone;
            
            // Reset transition timer
            this.idleTransitionStartTime = 0;
            
            // Ensure we're starting at the exact current position
            this.idleStartProgress = this.targetProgressPhone;
        }
    }

    init() {
        // Calculate plane size based on camera FOV and distance
        const fov = this.camera.fov * (Math.PI / 180); // Convert to radians
        const distance = Math.abs(this.camera.position.z);
        const height = 2 * Math.tan(fov / 2) * distance;
        const width = height * this.sizes.aspectRatio;
        
        // Use responsive scale instead of fixed 0.8
        const planeSize = Math.min(height, width) * this.currentScale;
        
        // Create plane geometry with equal width and height for a square
        const geometry = new PlaneGeometry(planeSize, planeSize);
        
        // Create shader material
        this.materialPhoneBackground = new ShaderMaterial({
            uniforms: {
                uDiffuseTexture: { value: this.resources.items.phoneAtlas },
                uProgress: { value: this.currentProgressPhone },
                uOrientation: { value: 0.0 },
                uOpacity: { value: 0.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true
        });

        this.materialPhoneForeground = new ShaderMaterial({
            uniforms: {
                uDiffuseTexture: { value: this.resources.items.phoneAtlas },
                uProgress: { value: this.currentProgressPhone },
                uOrientation: { value: 1.0 },
                uOpacity: { value: 0.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true
        });

        this.materialTriangle = new ShaderMaterial({
            uniforms: {
                uTime: { value: 0.0 },
                uTriangleTexture: { value: this.resources.items.triangle },
                uTriangleBlurTexture: { value: this.resources.items.triangleBlur },
                uTriangleBlur2Texture: { value: this.resources.items.triangleBlur2 },
                uGeneralOpacity: { value: 1.0 },
                uFloatingAmplitude: { value: new Vector2(0.01, 0.015) }, // Small X,Y amplitude
                uFloatingSpeed: { value: 0.4 }, // Slow speed for subtle movement
                uProgress: { value: 0.5 }, // Default to middle (no rotation)
                uRotationCenter: { value: new Vector2(0, 0.3) }, // Default rotation center
                uRotationMax: { value: 0.05 }, // Maximum rotation in radians (about 11.5 degrees)
            },
            vertexShader: triangleVertexShader,
            fragmentShader: triangleFragmentShader,
            transparent: true
        });

        this.materialTitleStartup = new ShaderMaterial({
            uniforms: {
                uTime: { value: 0.0 },
                uDiffuseTexture: { value: this.resources.items.titleStartup },
                uOpacity: { value: 0.0 }
            },
            vertexShader: startupVertexShader,
            fragmentShader: startupFragmentShader,
            transparent: true
        });
        
        this.materialTitleHotline = new ShaderMaterial({
            uniforms: {
                uTime: { value: 0.0 },
                uDiffuseTexture: { value: this.resources.items.titleHotline },
                uOpacity: { value: 0.0 }
            },
            vertexShader: hotlineVertexShader,
            fragmentShader: hotlineFragmentShader,
            transparent: true
        });
        
        // Create mesh
        this.phoneBackgroundMesh = new Mesh(geometry, this.materialPhoneBackground);
        this.add(this.phoneBackgroundMesh);

        this.triangleMesh = new Mesh(geometry, this.materialTriangle);
        this.add(this.triangleMesh);
    
        this.phoneForegroundMesh = new Mesh(geometry, this.materialPhoneForeground);
        this.add(this.phoneForegroundMesh);
        
        // Create star meshes
        this.createStars(planeSize);
        
        // Create title mesh with correct aspect ratio
        this.createTitleMesh(planeSize);
        
        // Create hotline title mesh
        this.createHotlineTitleMesh(planeSize);

        // Set initial position
        this.updatePosition();
        
        // Set initial visibility
        this.updateVisibility();
    }
    
    createTitleMesh(phonePlaneSize) {
        // Get the startup title texture
        const titleTexture = this.resources.items.titleStartup;
        
        // Calculate aspect ratio of the texture
        const imageAspectRatio = titleTexture.image.width / titleTexture.image.height;
        
        // Calculate width and height based on phone size and aspect ratio
        const titleWidth = phonePlaneSize * this.titleStartup.scaleCoef;
        const titleHeight = titleWidth / imageAspectRatio;
        
        // Create geometry with correct aspect ratio
        const titleGeometry = new PlaneGeometry(titleWidth, titleHeight);
        
        // Create mesh
        this.titleStartupMesh = new Mesh(titleGeometry, this.materialTitleStartup);
        
        // Position in center of group (z slightly in front of triangle)
        this.titleStartupMesh.position.z = 0.01;
        
        // Set initial position with shift
        const initialShiftX = this.titleStartup.initialShift.x * phonePlaneSize;
        const initialShiftY = this.titleStartup.initialShift.y * phonePlaneSize;
        this.titleStartupMesh.position.x = phonePlaneSize * this.titleStartup.position.x + initialShiftX;
        this.titleStartupMesh.position.y = phonePlaneSize * this.titleStartup.position.y + initialShiftY;
        
        // Add to group
        this.add(this.titleStartupMesh);
    }
    
    createHotlineTitleMesh(phonePlaneSize) {
        // Get the hotline title texture
        const titleTexture = this.resources.items.titleHotline;
        
        // Calculate aspect ratio of the texture
        const imageAspectRatio = titleTexture.image.width / titleTexture.image.height;
        
        // Calculate width and height based on phone size and aspect ratio
        const titleWidth = phonePlaneSize * this.titleHotline.scaleCoef;
        const titleHeight = titleWidth / imageAspectRatio;
        
        // Create geometry with correct aspect ratio
        const titleGeometry = new PlaneGeometry(titleWidth, titleHeight);
        
        // Create mesh
        this.titleHotlineMesh = new Mesh(titleGeometry, this.materialTitleHotline);
        
        // Position in center of group (z slightly in front of triangle)
        this.titleHotlineMesh.position.z = 0.01;
        
        // Set initial position with shift
        const initialShiftX = this.titleHotline.initialShift.x * phonePlaneSize;
        const initialShiftY = this.titleHotline.initialShift.y * phonePlaneSize;
        this.titleHotlineMesh.position.x = phonePlaneSize * this.titleHotline.position.x + initialShiftX;
        this.titleHotlineMesh.position.y = phonePlaneSize * this.titleHotline.position.y + initialShiftY;
        
        // Add to group
        this.add(this.titleHotlineMesh);
    }
    
    createStars(phonePlaneSize) {
        // Create all star meshes based on the stars array
        this.starMeshes = [];
        
        for (const star of this.stars) {
            // Get the star texture
            const starTexture = this.resources.items[star.texture];
            
            // Calculate size based on phone size and star's size percentage
            const baseSize = phonePlaneSize * star.size;
            
            // Calculate aspect ratio of the texture
            const imageAspectRatio = starTexture.image.width / starTexture.image.height;
            
            // Calculate width and height based on the aspect ratio
            // We'll use the size parameter as the maximum dimension
            let starWidth, starHeight;
            
            if (imageAspectRatio >= 1) {
                // Wider than tall
                starWidth = baseSize;
                starHeight = baseSize / imageAspectRatio;
            } else {
                // Taller than wide
                starWidth = baseSize * imageAspectRatio;
                starHeight = baseSize;
            }
            
            // Create geometry with correct aspect ratio
            const starGeometry = new PlaneGeometry(starWidth, starHeight);
            
            // Create basic material with the star texture
            const starMaterial = new MeshBasicMaterial({
                map: starTexture,
                transparent: true,
                opacity: 0, // Start with zero opacity
                depthWrite: false,
                blending: AdditiveBlending
            });
            
            // Create mesh
            const starMesh = new Mesh(starGeometry, starMaterial);
            
            // Set position
            starMesh.position.x = phonePlaneSize * star.position.x;
            starMesh.position.y = phonePlaneSize * star.position.y;
            starMesh.position.z = star.position.z;
            
            // Set initial scale (will be used for animation later)
            starMesh.scale.set(0, 0, 1); // Start with zero scale
            
            // Store reference to the star config
            starMesh.userData.starConfig = star;
            
            // Add random animation offset for each star (between 0 and 2.0 seconds)
            starMesh.userData.animationDelay = Math.random() * 2.0;
            
            // Random animation speed multiplier (between 0.7 and 1.3)
            starMesh.userData.animationSpeed = 0.7 + Math.random() * 0.6;
            
            // Add to group
            this.add(starMesh);
            
            // Store in array for easy access
            this.starMeshes.push(starMesh);
        }
    }
    
    updateIdleAnimation(delta) {
        // Update transition time
        this.idleTransitionStartTime += delta;
        
        // Increment idle animation time
        this.idleAnimationTime += delta * this.idleAnimationSpeed;
        
        // Calculate a transition factor (0 at start, 1 when fully transitioned)
        // Use Power easing function (equivalent to GSAP's Power1.easeInOut for power=1)
        const t = Math.min(this.idleTransitionStartTime / this.idleTransitionDuration, 1.0);
        const transitionFactor = this.easeInOut(t, this.idleEasingPower);
        
        // Calculate the oscillation center - smoothly transition from last position to center
        const oscillationCenter = MathUtils.lerp(
            this.lastNonIdletargetProgressPhone,
            this.mouseControlCenterProgress,
            transitionFactor
        );
        
        // Calculate oscillation amplitude - gradually increase from 0 to full range
        const currentRange = this.idleAnimationRange * transitionFactor;
        
        // Calculate sine wave oscillation with gradually increasing amplitude
        const oscillation = Math.sin(this.idleAnimationTime) * currentRange;
        
        // Apply oscillation around the transitioning center
        this.targetProgressPhone = oscillationCenter + oscillation;
        
        // Ensure within valid range
        this.targetProgressPhone = MathUtils.clamp(
            this.targetProgressPhone, 
            this.mouseControlRangeStart, 
            this.mouseControlRangeEnd
        );
    }
    
    updateFloatingAnimation(delta) {
        if (!this.phoneBackgroundMesh || !this.phoneForegroundMesh || !this.enableFloating) {
            // Reset position if floating is disabled or meshes don't exist
            if (this.phoneBackgroundMesh) this.phoneBackgroundMesh.position.set(0, 0, 0);
            if (this.phoneForegroundMesh) this.phoneForegroundMesh.position.set(0, 0, 0);
            return;
        }
        
        // Increment the floating animation time
        this.floatingAnimationTime += delta * this.floatingSpeed;
        
        // Calculate figure-8 pattern movement
        // The figure-8 pattern is created by using sin(t) for X axis and sin(2*t) for Y axis
        const x = Math.cos(this.floatingAnimationTime) * this.floatingAmplitudeX;
        const y = Math.sin(this.floatingAnimationTime) * this.floatingAmplitudeY;
        
        // Apply the position offset to phone meshes only (not the triangle)
        this.phoneBackgroundMesh.position.x = x;
        this.phoneBackgroundMesh.position.y = y;
        this.phoneForegroundMesh.position.x = x;
        this.phoneForegroundMesh.position.y = y;
    }
    
    updateMouseControl() {
        // Calculate phone center in normalized screen coords (0-1)
        const phoneScreenPos = new Vector3();
        this.phoneBackgroundMesh.getWorldPosition(phoneScreenPos);
        phoneScreenPos.project(this.camera);
        const phoneNormX = (phoneScreenPos.x * 0.5) + 0.5;

        // Get normalized mouse X (interpolated for smoothness)
        const mouseNormX = this.input.interpolatedNormX;

        // Calculate difference from phone center
        const diffX = mouseNormX - phoneNormX;
        const absDiffX = Math.abs(diffX);
        
        // Optimization: pre-calculate the doubled value used in both power functions
        const scaledDiff = absDiffX * 2;
        
        // Apply different power functions based on direction
        // Optimization: Use ternary operator for cleaner code
        const sensitivity = diffX > 0 ? this.sensitivityPowerRight : this.sensitivityPowerLeft;
        const influence = MathUtils.clamp(Math.pow(scaledDiff, sensitivity), 0, 1);
        
        // Optimization: Pre-calculate common values
        const centerProgress = this.mouseControlCenterProgress;
        
        // Map influence to the defined range halves
        // Calculate the target progress based on mouse direction
        if (diffX > 0) {
            // Mouse moved right but phone rotates left (toward START of range)
            const rangeHalf = centerProgress - this.mouseControlRangeStart;
            this.targetProgressPhone = centerProgress - (influence * rangeHalf);
            
            // Map phone progress from its range to triangle range
            const normalizedProgress = (this.targetProgressPhone - this.mouseControlRangeStart) / 
                (this.mouseControlRangeEnd - this.mouseControlRangeStart);
            // Invert the mapping for correct rotation direction
            this.targetProgressTriangle = this.mouseControlTriangleRangeEnd - 
                normalizedProgress * (this.mouseControlTriangleRangeEnd - this.mouseControlTriangleRangeStart);
        } else {
            // Mouse moved left but phone rotates right (toward END of range)
            const rangeHalf = this.mouseControlRangeEnd - centerProgress;
            this.targetProgressPhone = centerProgress + (influence * rangeHalf);
            
            // Map phone progress from its range to triangle range
            const normalizedProgress = (this.targetProgressPhone - this.mouseControlRangeStart) / 
                (this.mouseControlRangeEnd - this.mouseControlRangeStart);
            // Invert the mapping for correct rotation direction
            this.targetProgressTriangle = this.mouseControlTriangleRangeEnd - 
                normalizedProgress * (this.mouseControlTriangleRangeEnd - this.mouseControlTriangleRangeStart);
        }
        
        // Clamp within the overall mouse control range
        this.targetProgressPhone = MathUtils.clamp(
            this.targetProgressPhone, 
            this.mouseControlRangeStart, 
            this.mouseControlRangeEnd
        );
        
        // Store this target progress for use when transitioning to idle
        this.lastNonIdletargetProgressPhone = this.targetProgressPhone;
    }
    
    updateStarAnimations(delta) {
        if (!this.starMeshes || this.starMeshes.length === 0) return;
        
        // Increment animation time
        this.starAnimState.time += delta;
        
        // Only start star animations after titles have fully appeared
        if (this.animationTime < this.starAnimationStartTime) return;
        
        // Update each star with its own animation cycle
        for (const starMesh of this.starMeshes) {
            // Get star config and animation parameters
            const config = starMesh.userData.starConfig;
            const delay = starMesh.userData.animationDelay;
            const speed = starMesh.userData.animationSpeed;
            
            // Calculate time offset by the delay
            const starTime = this.starAnimState.time - delay;
            
            // Skip if animation hasn't started yet
            if (starTime < 0) continue;
            
            // Generate noise value unique to this star (range -1 to 1)
            // Use 4D noise for more variety (x, y, z, w)
            // Multiply by star index for different phases
            const starIndex = this.starMeshes.indexOf(starMesh);
            const noiseX = this.simplex.noise(
                this.starAnimState.noiseOffset + starIndex * 0.1, 
                starTime * 0.2 * speed, 
                0, 
                0
            );
            
            // Remap noise from [-1, 1] to [0, 1] 
            const noiseValue = (noiseX + 1) * 0.5;
            
            // Use noise to create a pulsing animation between 0 and max values
            // This creates a more natural, less predictable animation
            // Apply bell curve effect using sin²(x) function
            const pulse = Math.sin(starTime * speed) ** 2;
            
            // Apply noise influence to make each pulse slightly different
            const finalPulse = pulse * (0.8 + noiseValue * 0.2);
            
            // Apply opacity based on the pulse
            starMesh.material.opacity = finalPulse * config.opacity;
            
            // Apply scale also based on the pulse, but with slightly different timing
            // Add small phase offset to create slight difference between scale and opacity
            const scalePulse = Math.sin((starTime + 0.2) * speed) ** 2 * (0.8 + noiseValue * 0.2);
            const scale = scalePulse * config.scale;
            starMesh.scale.set(scale, scale, 1);
        }
    }

    updateVisibility() {
        const isSmallScreen = this.sizes.width < this.contentWrapper.maxWidth;
        
        // Set orientation to show full phone on small screens
        if (this.materialPhoneBackground) {
            this.materialPhoneBackground.uniforms.uOrientation.value = isSmallScreen ? 1.0 : 0.0;
        }
        if (this.materialPhoneForeground) {
            this.materialPhoneForeground.uniforms.uOrientation.value = isSmallScreen ? 1.0 : 1.0;
        }
        
        // Hide/show elements based on screen size
        if (this.triangleMesh) {
            this.triangleMesh.visible = !isSmallScreen;
        }
        if (this.phoneForegroundMesh) {
            this.phoneForegroundMesh.visible = !isSmallScreen;
        }
        if (this.titleStartupMesh) {
            this.titleStartupMesh.visible = !isSmallScreen;
        }
        if (this.titleHotlineMesh) {
            this.titleHotlineMesh.visible = !isSmallScreen;
        }
        
        // Hide/show all stars
        if (this.starMeshes) {
            this.starMeshes.forEach(starMesh => {
                starMesh.visible = !isSmallScreen;
            });
        }
    }

    update(delta) {
        if (!this.materialPhoneBackground || !this.materialPhoneForeground || !this.materialTriangle) return;
        
        // Apply floating animation (independent of other animations)
        this.updateFloatingAnimation(delta);

        // Update animation time
        this.animationTime += delta;

        // Determine the animation state and update target progress accordingly
        if (this.isAppearing) {
            // --- PHASE 1: Initial Appear Animation --- 
            // Linear appear animation from 0 to the mouse control center point
            this.targetProgressPhone = Math.min(this.animationTime / this.appearDuration, 1.0) * this.mouseControlCenterProgress; 
            
            // Animate triangle opacity during appear phase
            this.triangleOpacity = Math.min(this.animationTime / this.appearDuration, 1.0);
            this.materialTriangle.uniforms.uGeneralOpacity.value = this.triangleOpacity;
            
            // Animate phone opacity - faster (70% of appear duration)
            this.phoneOpacity = Math.min(this.animationTime / (this.appearDuration * 0.7), 1.0);
            this.materialPhoneBackground.uniforms.uOpacity.value = this.phoneOpacity;
            this.materialPhoneForeground.uniforms.uOpacity.value = this.phoneOpacity;

            // Check if we've completed the appear animation
            if (this.animationTime >= this.appearDuration) {
                this.isAppearing = false;
                this.targetProgressPhone = this.mouseControlCenterProgress;
                this.triangleOpacity = 1.0;
                this.phoneOpacity = 1.0;
            }
        } 
        else if (this.isIdleAnimation) {
            // --- PHASE 2: Idle Animation ---
            this.updateIdleAnimation(delta);
        }
        else if (this.input.hasMouseMoved) {
            // --- PHASE 3: Mouse Control (only if mouse has moved) ---
            this.updateMouseControl();
        }
        else {
            // --- FALLBACK: Default to center position ---
            // This should rarely happen, but provides a safety default
            this.targetProgressPhone = this.mouseControlCenterProgress;
            this.targetProgressTriangle = this.mouseControlCenterProgress;
        }

        // Handle title animations separately from phone animation
        this.updateTitleAnimation();
        this.updateHotlineTitleAnimation();
        
        // Update star animations
        this.updateStarAnimations(delta);

        // --- Apply Smoothing --- 
        // Adjust lerp factor based on distance to target for faster settling
        const lerpAdjust = 1.0 - Math.abs(this.currentProgressPhone - this.targetProgressPhone);
        const adjustedLerpFactor = this.lerpFactor * (1.0 + lerpAdjust * 2.0);
        this.currentProgressPhone = MathUtils.lerp(this.currentProgressPhone, this.targetProgressPhone, adjustedLerpFactor);

        const lerpAdjustTriangle = 1.0 - Math.abs(this.currentProgressTriangle - this.targetProgressTriangle);
        const adjustedLerpFactorTriangle = this.lerpFactorTriangle * (1.0 + lerpAdjustTriangle * 2.0);
        this.currentProgressTriangle = MathUtils.lerp(this.currentProgressTriangle, this.targetProgressTriangle, adjustedLerpFactorTriangle);
        
        // --- Update Shader --- 
        this.materialPhoneBackground.uniforms.uProgress.value = this.currentProgressPhone;
        this.materialPhoneForeground.uniforms.uProgress.value = this.currentProgressPhone;

        this.materialTriangle.uniforms.uTime.value += delta;
        this.materialTriangle.uniforms.uProgress.value = this.currentProgressTriangle;

        if (this.materialTitleStartup) {
            this.materialTitleStartup.uniforms.uTime.value += delta;
        }
        if (this.materialTitleHotline) {
            this.materialTitleHotline.uniforms.uTime.value += delta;
        }
    }

    updateTitleAnimation() {
        // Only process if title is still animating
        if (!this.titleStartup.isAnimating || !this.materialTitleStartup || !this.titleStartupMesh) return;

        // Calculate how far into title animation we are (considering delay)
        const titleAnimationTime = this.animationTime - this.titleStartup.animationStartDelay;
        
        // If we haven't reached the start delay yet, do nothing
        if (titleAnimationTime <= 0) return;
        
        // Calculate progress of title animation
        const titleProgress = Math.min(titleAnimationTime / this.titleStartup.animationDuration, 1.0);
        
        // Update opacity for shader
        this.titleStartup.opacity = titleProgress;
        this.materialTitleStartup.uniforms.uOpacity.value = this.titleStartup.opacity;
        
        // Calculate current position based on progress
        // Start at initialPosition (position + shift) and move to finalPosition (position)
        const phonePlaneSize = this.phoneBackgroundMesh ? this.phoneBackgroundMesh.geometry.parameters.width : 1.0;
        const shiftX = this.titleStartup.initialShift.x * (1.0 - titleProgress) * phonePlaneSize;
        const shiftY = this.titleStartup.initialShift.y * (1.0 - titleProgress) * phonePlaneSize;
        
        // Apply position with shift
        this.titleStartupMesh.position.x = phonePlaneSize * this.titleStartup.position.x + shiftX;
        this.titleStartupMesh.position.y = phonePlaneSize * this.titleStartup.position.y + shiftY;
        
        // Check if title animation is complete
        if (titleProgress >= 1.0) {
            this.titleStartup.isAnimating = false;
            
            // Set final position explicitly to avoid floating point errors
            this.titleStartupMesh.position.x = phonePlaneSize * this.titleStartup.position.x;
            this.titleStartupMesh.position.y = phonePlaneSize * this.titleStartup.position.y;
        }
    }

    updateHotlineTitleAnimation() {
        // Only process if title is still animating
        if (!this.titleHotline.isAnimating || !this.materialTitleHotline || !this.titleHotlineMesh) return;

        // Calculate how far into title animation we are (considering delay)
        const titleAnimationTime = this.animationTime - this.titleHotline.animationStartDelay;
        
        // If we haven't reached the start delay yet, do nothing
        if (titleAnimationTime <= 0) return;
        
        // Calculate progress of title animation
        const titleProgress = Math.min(titleAnimationTime / this.titleHotline.animationDuration, 1.0);
        
        // Update opacity for shader
        this.titleHotline.opacity = titleProgress;
        this.materialTitleHotline.uniforms.uOpacity.value = this.titleHotline.opacity;
        
        // Calculate current position based on progress
        // Start at initialPosition (position + shift) and move to finalPosition (position)
        const phonePlaneSize = this.phoneBackgroundMesh ? this.phoneBackgroundMesh.geometry.parameters.width : 1.0;
        const shiftX = this.titleHotline.initialShift.x * (1.0 - titleProgress) * phonePlaneSize;
        const shiftY = this.titleHotline.initialShift.y * (1.0 - titleProgress) * phonePlaneSize;
        
        // Apply position with shift
        this.titleHotlineMesh.position.x = phonePlaneSize * this.titleHotline.position.x + shiftX;
        this.titleHotlineMesh.position.y = phonePlaneSize * this.titleHotline.position.y + shiftY;
        
        // Check if title animation is complete
        if (titleProgress >= 1.0) {
            this.titleHotline.isAnimating = false;
            
            // Set final position explicitly to avoid floating point errors
            this.titleHotlineMesh.position.x = phonePlaneSize * this.titleHotline.position.x;
            this.titleHotlineMesh.position.y = phonePlaneSize * this.titleHotline.position.y;
        }
    }

    updatePosition() {
        if (!this.camera || !this.phoneBackgroundMesh) return;
        
        // Get viewport dimensions in THREE.js units at camera position
        const fov = this.camera.fov * (Math.PI / 180); // Convert to radians
        const cameraDistance = Math.abs(this.camera.position.z);
        const viewportHeight = 2 * Math.tan(fov / 2) * cameraDistance;
        const viewportWidth = viewportHeight * this.sizes.aspectRatio;
        
        // Calculate pixel-to-unit conversion factor
        const pixelsPerUnit = this.sizes.width / viewportWidth;
        
        // Check if on mobile using Sizes.isMobile
        if (this.sizes.isMobile) {
            // MOBILE APPROACH (less than 768px):
            // Position the phone in the middle horizontally and at the bottom with padding
            
            // Set X position to center (0 in Three.js coordinates)
            this.position.x = 0;
            
            // Calculate bottom edge of screen in units (from center)
            const screenBottomEdge = -viewportHeight / 2;
            
            // Convert 60px padding to THREE.js units
            const bottomPaddingUnits = 60 / pixelsPerUnit;
            
            // Get the phone height
            const phoneHeightUnits = this.phoneBackgroundMesh.geometry.parameters.height;
            const phoneHalfHeightUnits = phoneHeightUnits / 2;
            
            // Position Y so bottom of phone is at (bottom edge + padding)
            // This places the phone's bottom edge at precisely (bottom edge + padding)
            this.position.y = screenBottomEdge + bottomPaddingUnits + phoneHalfHeightUnits;
        } else {
            // Check if screen is smaller than max wrapper width
            const isSmallScreen = this.sizes.width < this.contentWrapper.maxWidth;
            
            if (isSmallScreen) {
                // SMALL SCREEN APPROACH:
                // Center the phone horizontally
                this.position.x = 0;
                this.position.y = 0;
            } else {
                // LARGE SCREEN APPROACH:
                // Position relative to content wrapper
                
                // Get the phone width in units
                const phoneWidthUnits = this.phoneBackgroundMesh.geometry.parameters.width;
                const phoneHalfWidthUnits = phoneWidthUnits / 2;
                
                // Use wrapper max width
                const wrapperWidth = this.contentWrapper.maxWidth;
                const wrapperHalfWidth = wrapperWidth / 2;
                
                // Convert to THREE.js units
                const wrapperHalfWidthUnits = wrapperHalfWidth / pixelsPerUnit;
                
                // Normal padding from wrapper edge (in pixels)
                const wrapperPadding = this.contentWrapper.padding;
                const wrapperPaddingUnits = wrapperPadding / pixelsPerUnit;
                
                // Position phone so its right edge is at (wrapper right edge - padding)
                const rightAlignedX = wrapperHalfWidthUnits - wrapperPaddingUnits - phoneHalfWidthUnits;
                
                // Set the position with a direct assignment
                this.position.x = rightAlignedX;
                // Reset Y position for non-mobile
                this.position.y = 0;
            }
        }
        
        // Force matrix update to ensure all children update
        this.updateMatrixWorld(true);
    }
} 